import { useCallback, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { WidgetConfigSiteTabbedShell } from '@/components/widgetConfigSiteTabbedShell/index.ts';
import { useLayoutStorage } from '@/stores/layout.ts';
import { WidgetConfigModalProps } from '@/types.ts';

import {
  cameraSelectorSingleSitePanelDataAttributes,
  cameraSelectorTabsDataAttributes,
} from './cameraSelector/cameraSelector.constants.ts';
import { CameraSitePanel } from './cameraSelector/CameraSitePanel.tsx';
import type { CameraWidgetConfig } from './cameraSelector/cameraWidgetConfig.types.ts';
import { focusInitialCameraSelectCard } from './cameraSelector/focusInitialCameraSelectCard.ts';
import { useCameraSelectorGamepadWindowEvents } from './cameraSelector/useCameraSelectorGamepadWindowEvents.ts';

export type { CameraWidgetConfig } from './cameraSelector/cameraWidgetConfig.types.ts';

type CameraConfigModalContentProps = WidgetConfigModalProps<CameraWidgetConfig> & {
  /** When true (switch camera from stream widget), initial focus lands on the configured camera. */
  focusSelectedCamera?: boolean;
  /** When false, tab content unmounts (e.g. while parent modal is closed). Defaults to true. */
  isOpen?: boolean;
};

export const CameraSelector = ({
  widgetId,
  onSave,
  onClose,
  onGoBack,
  setModalTitle,
  focusSelectedCamera = false,
  isOpen = true,
}: CameraConfigModalContentProps) => {
  const { t } = useTranslation(['landingPage']);

  const storedConfig = useLayoutStorage(
    useShallow(s => (widgetId ? s.getWidgetConfigProps<CameraWidgetConfig>(widgetId) : undefined))
  );
  const preferredCameraId =
    focusSelectedCamera && storedConfig?.camera.id ? storedConfig.camera.id : undefined;

  useCameraSelectorGamepadWindowEvents({ isOpen, preferredCameraId });

  useEffect(() => {
    setModalTitle?.(t('landingPage:selectCamera'));
  }, [setModalTitle, t]);

  const focusInitialCamera = useCallback(() => {
    focusInitialCameraSelectCard(preferredCameraId);
  }, [preferredCameraId]);

  return (
    <WidgetConfigSiteTabbedShell
      source={{ kind: 'cameras' }}
      widgetId={widgetId}
      groupBy='parent'
      defaultInventoryId={preferredCameraId}
      isOpen={isOpen}
      emptyMessage={t('landingPage:noCamerasAvailable')}
      tabsDataAttributes={cameraSelectorTabsDataAttributes}
      singleSitePanelDataAttributes={cameraSelectorSingleSitePanelDataAttributes}
      onPanelsReady={focusInitialCamera}
      onSave={onSave}
      onClose={onClose}
      onGoBack={onGoBack}
      renderSitePanel={() => <CameraSitePanel />}
    />
  );
};
